import React, {DragEvent, FC, useEffect} from 'react';
import {DragPreviewOptions, useDrag} from 'react-dnd';

import {supportsDownloadDragSource} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {DragEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import conversationDragAndDrop1 from '../../../assets/images/conversationDragAndDrop1.svg';
import {addDownloadToDragEvent} from '../../../helpers/dragAndDropHelpers';
import {emlFileName} from '../../../helpers/models/messageHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {MessageModel} from '../../../models/api/messageModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {
  addNativeAttachmentsToDragEvent,
  DraggedAttachmentItemTypesEnum,
} from '../../common/attachments/attachmentsDragAndDrop';
import {computeCellDragSourceMessageAttachments} from './cellDragSourceHelpers';

/*
 * Constants & Types.
 */

const MessageCellDragType = 'MESSAGE_CELL';

/*
 * Component.
 */

interface MessageCellDragSourceProps {
  message: MessageModel;
  isSelected?: boolean;
  selectedMessages: ReadonlyArray<MessageModel>;
}

export const MessageCellDragSource: FC<MessageCellDragSourceProps> = (props) => {
  const {message} = props;
  const companyUrl = useAppSelector(getCompanyUrl);

  const [, drag, preview] = useDrag({type: MessageCellDragType, item: {}});

  const onDragEnd: DragEventHandler = (e) => {
    if (!e.dataTransfer || e.dataTransfer.dropEffect !== 'copy') {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.MESSAGE_CELL_DRAG_AND_DROP_TO_EML);
  };

  useEffect(() => {
    const previewOptions: DragPreviewOptions = {
      offsetX: 10,
      offsetY: 10,
    };

    const previewImage = new Image();
    previewImage.src = conversationDragAndDrop1;
    preview(previewImage, previewOptions);
  }, [preview]);

  const onDragStart = (e: DragEvent) => {
    if (supportsDownloadDragSource()) {
      addDownloadToDragEvent(e, {
        name: emlFileName(message),
        url: `${location.origin}${companyUrl}/messages/${message.uid}/eml`,
      });
    }

    addNativeAttachmentsToDragEvent(e, {
      type: DraggedAttachmentItemTypesEnum.MESSAGES,
      messages: computeCellDragSourceMessageAttachments(
        props.isSelected ? props.selectedMessages : [props.message],
      ),
    });
  };

  return (
    <div ref={drag} onDragEnd={onDragEnd} onDragStart={onDragStart}>
      {props.children}
    </div>
  );
};
